import React, { useState, useCallback, useEffect, useRef } from 'react';
import Canvas from './components/Canvas';
import Toolbar from './components/Toolbar';
import Sidebar from './components/Sidebar';
import {
  NewProjectModal,
  EditNodeModal,
  EditConnectionModal,
  ExportModal,
  RecentProjectsModal,
  BulkEditModal,
  EditGroupModal,
  EditSwimlaneModal
} from './components/Modals';
import { useProject } from './hooks/useProject';
import { createProjectFile, downloadAsJSON, importFromJSON, addToRecentProjects } from './utils/exportUtils';
import { autoLayout, alignNodes, distributeNodes } from './utils/layoutUtils';

const App = () => {
  // Project state
  const {
    projectName,
    setProjectName,
    nodes,
    connections,
    groups,
    swimlanes,
    selectedNodeId,
    setSelectedNodeId,
    selectedNodeIds,
    selectNode,
    clearSelection,
    selectedGroupId,
    selectGroup,
    selectedConnectionId,
    setSelectedConnectionId,
    selectedSwimlaneId,
    selectSwimlane,
    connectingFrom,
    canvas,
    setCanvas,
    clipboard,
    addNode,
    updateNode,
    updateNodes,
    deleteNode,
    deleteSelectedNodes,
    copyNode,
    copySelectedNodes,
    pasteNodes,
    addConnection,
    updateConnection,
    deleteConnection,
    createGroup,
    updateGroup,
    deleteGroup,
    addSwimlane,
    updateSwimlane,
    deleteSwimlane,
    reorderSwimlanes,
    toggleGrid,
    startConnecting,
    completeConnecting,
    cancelConnecting,
    loadTemplate,
    loadProject,
    resetProject,
    undo,
    redo,
    canUndo,
    canRedo
  } = useProject();

  // UI state
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Modal states
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showEditNodeModal, setShowEditNodeModal] = useState(false);
  const [showEditConnectionModal, setShowEditConnectionModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showRecentProjectsModal, setShowRecentProjectsModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showEditSwimlaneModal, setShowEditSwimlaneModal] = useState(false);

  // Refs
  const fileInputRef = useRef(null);
  const canvasSvgRef = useRef(null);
  const nameInputRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get selected node/connection/group/swimlane for modals
  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const selectedConnection = connections.find(c => c.id === selectedConnectionId);
  const selectedGroup = groups.find(g => g.id === selectedGroupId);
  const selectedSwimlane = swimlanes.find(s => s.id === selectedSwimlaneId);

  // Dark mode effect
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Escape - cancel connecting or deselect
      if (e.key === 'Escape') {
        if (connectingFrom) {
          cancelConnecting();
        } else {
          clearSelection();
        }
        return;
      }

      // Delete/Backspace - delete selected
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodeIds.length > 0) {
          deleteSelectedNodes();
        } else if (selectedConnectionId) {
          deleteConnection(selectedConnectionId);
        }
        return;
      }

      // Ctrl/Cmd shortcuts
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 's':
            e.preventDefault();
            handleSave();
            break;
          case 'n':
            e.preventDefault();
            setShowNewProjectModal(true);
            break;
          case 'o':
            e.preventDefault();
            fileInputRef.current?.click();
            break;
          case 'e':
            e.preventDefault();
            setShowExportModal(true);
            break;
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              redo();
            } else {
              undo();
            }
            break;
          case 'c':
            e.preventDefault();
            if (selectedNodeIds.length > 0) {
              copySelectedNodes();
            }
            break;
          case 'v':
            e.preventDefault();
            if (clipboard.nodes.length > 0) {
              pasteNodes();
            }
            break;
          case 'a':
            e.preventDefault();
            // Select all nodes
            if (nodes.length > 0) {
              nodes.forEach((node, index) => {
                selectNode(node.id, index > 0);
              });
            }
            break;
          case 'f':
            e.preventDefault();
            // Focus search input
            searchInputRef.current?.focus();
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeIds, selectedConnectionId, connectingFrom, cancelConnecting, deleteSelectedNodes, deleteConnection, undo, redo, copySelectedNodes, pasteNodes, clipboard, nodes, selectNode, clearSelection]);

  // Handlers
  const handleSave = useCallback(() => {
    const projectData = createProjectFile(projectName, nodes, connections, canvas);
    downloadAsJSON(projectData, projectName);
    addToRecentProjects(projectData);
  }, [projectName, nodes, connections, canvas]);

  const handleImport = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const projectData = await importFromJSON(file);
      loadProject(projectData);
    } catch (err) {
      alert(err.message);
    }

    // Reset file input
    e.target.value = '';
  }, [loadProject]);

  const handleCreateProject = useCallback((name, templateKey) => {
    resetProject();
    setProjectName(name);
    loadTemplate(templateKey);
  }, [resetProject, setProjectName, loadTemplate]);

  const handleAddNode = useCallback((type) => {
    // Add node in center of visible canvas area (works with infinite canvas)
    // Get approximate center of visible area
    const viewportCenterX = (window.innerWidth / 2 - canvas.panX) / canvas.zoom;
    const viewportCenterY = (window.innerHeight / 2 - canvas.panY) / canvas.zoom;

    // Add some randomness to avoid stacking
    const x = viewportCenterX - 70 + Math.random() * 100;
    const y = viewportCenterY - 30 + Math.random() * 60;

    // Snap to grid
    addNode(type, Math.round(x / 20) * 20, Math.round(y / 20) * 20);
  }, [addNode, canvas]);

  const handleCanvasClick = useCallback(() => {
    clearSelection();
    if (connectingFrom) {
      cancelConnecting();
    }
  }, [clearSelection, connectingFrom, cancelConnecting]);

  const handleSelectNode = useCallback((id, addToSelection = false) => {
    selectNode(id, addToSelection);
  }, [selectNode]);

  const handleSelectConnection = useCallback((id) => {
    setSelectedConnectionId(id);
    clearSelection();
  }, [setSelectedConnectionId, clearSelection]);

  const handleDoubleClickNode = useCallback((id) => {
    setSelectedNodeId(id);
    setShowEditNodeModal(true);
  }, [setSelectedNodeId]);

  const handleDoubleClickConnection = useCallback((id) => {
    setSelectedConnectionId(id);
    setShowEditConnectionModal(true);
  }, [setSelectedConnectionId]);

  // Handle connection completion with auto-prompt for decision diamonds
  const handleCompleteConnecting = useCallback((toNodeId) => {
    if (!connectingFrom) return;

    // Check if the source node is a diamond (decision)
    const sourceNode = nodes.find(n => n.id === connectingFrom);
    const isDiamond = sourceNode?.type === 'diamond';

    // Complete the connection and get the new connection
    const newConnection = completeConnecting(toNodeId);

    // If source was a diamond, auto-open the connection editor
    if (isDiamond && newConnection) {
      setSelectedConnectionId(newConnection.id);
      setShowEditConnectionModal(true);
    }
  }, [connectingFrom, nodes, completeConnecting, setSelectedConnectionId]);

  const handleZoomChange = useCallback((zoom) => {
    setCanvas(prev => ({ ...prev, zoom }));
  }, [setCanvas]);

  const handlePanChange = useCallback(({ panX, panY }) => {
    setCanvas(prev => ({ ...prev, panX, panY }));
  }, [setCanvas]);

  // Layout handlers
  const handleAutoLayout = useCallback((direction = 'TB') => {
    if (nodes.length === 0) return;
    const layoutedNodes = autoLayout(nodes, connections, { direction });
    // Update all nodes with new positions
    const updates = layoutedNodes.map(node => ({
      id: node.id,
      changes: { x: node.x, y: node.y }
    }));
    updateNodes(updates);
  }, [nodes, connections, updateNodes]);

  const handleAlign = useCallback((alignment) => {
    if (selectedNodeIds.length < 2) return;
    const selectedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
    const alignedNodes = alignNodes(selectedNodes, alignment);
    const updates = alignedNodes.map(node => ({
      id: node.id,
      changes: { x: node.x, y: node.y }
    }));
    updateNodes(updates);
  }, [selectedNodeIds, nodes, updateNodes]);

  const handleDistribute = useCallback((direction) => {
    if (selectedNodeIds.length < 3) return;
    const selectedNodes = nodes.filter(n => selectedNodeIds.includes(n.id));
    const distributedNodes = distributeNodes(selectedNodes, direction);
    const updates = distributedNodes.map(node => ({
      id: node.id,
      changes: { x: node.x, y: node.y }
    }));
    updateNodes(updates);
  }, [selectedNodeIds, nodes, updateNodes]);

  // Bulk edit handler
  const handleBulkEdit = useCallback((nodeIds, changes) => {
    const updates = nodeIds.map(id => ({ id, changes }));
    updateNodes(updates);
  }, [updateNodes]);

  // Group handlers
  const handleCreateGroup = useCallback(() => {
    if (selectedNodeIds.length >= 1) {
      createGroup(selectedNodeIds, 'Group');
    }
  }, [selectedNodeIds, createGroup]);

  const handleDoubleClickGroup = useCallback((id) => {
    selectGroup(id);
    setShowEditGroupModal(true);
  }, [selectGroup]);

  // Swimlane handlers
  const handleAddSwimlane = useCallback(() => {
    addSwimlane('New Lane');
  }, [addSwimlane]);

  const handleDoubleClickSwimlane = useCallback((id) => {
    selectSwimlane(id);
    setShowEditSwimlaneModal(true);
  }, [selectSwimlane]);

  const handleNameSubmit = useCallback(() => {
    setIsEditingName(false);
  }, []);

  // Search handler
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    const results = nodes.filter(node =>
      node.text.toLowerCase().includes(query.toLowerCase()) ||
      (node.notes && node.notes.toLowerCase().includes(query.toLowerCase()))
    );
    setSearchResults(results);
    setShowSearchResults(true);
  }, [nodes]);

  const handleSelectSearchResult = useCallback((nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      // Select the node
      selectNode(nodeId);
      // Pan to center on the node
      const centerX = node.x + node.width / 2;
      const centerY = node.y + node.height / 2;
      const newPanX = (window.innerWidth / 2) - (centerX * canvas.zoom);
      const newPanY = (window.innerHeight / 2) - (centerY * canvas.zoom);
      setCanvas(prev => ({ ...prev, panX: newPanX, panY: newPanY }));
    }
    setShowSearchResults(false);
    setSearchQuery('');
  }, [nodes, selectNode, canvas.zoom, setCanvas]);

  return (
    <div className={`h-screen flex flex-col bg-gray-100 dark:bg-gray-900 ${isDarkMode ? 'dark' : ''}`}>
      {/* Header */}
      <header className="h-14 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 flex-shrink-0">
        {/* Logo & Project Name */}
        <div className="flex items-center gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="text-lg font-bold text-gray-900 dark:text-white">Flowise</span>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

          {/* Project Name */}
          {isEditingName ? (
            <input
              ref={nameInputRef}
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
              className="px-2 py-1 text-sm font-medium border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              autoFocus
            />
          ) : (
            <button
              onClick={() => setIsEditingName(true)}
              className="flex items-center gap-1.5 px-2 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              {projectName}
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

          {/* Search */}
          <div className="relative">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-gray-400 absolute left-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                placeholder="Search nodes... (Ctrl+F)"
                className="w-48 pl-9 pr-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white placeholder-gray-400"
              />
            </div>
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-64 overflow-y-auto">
                {searchResults.map((node) => (
                  <button
                    key={node.id}
                    onClick={() => handleSelectSearchResult(node.id)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <div
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: node.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {node.text.split('\n')[0]}
                      </div>
                      {node.notes && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {node.notes.substring(0, 30)}...
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showSearchResults && searchQuery && searchResults.length === 0 && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 p-3 text-sm text-gray-500 dark:text-gray-400">
                No nodes found
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* New Project */}
          <button
            onClick={() => setShowNewProjectModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="New Project (Ctrl+N)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">New</span>
          </button>

          {/* Open */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Open Project (Ctrl+O)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
            </svg>
            <span className="hidden sm:inline">Open</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".flowise,.json"
            onChange={handleImport}
            className="hidden"
          />

          {/* Recent */}
          <button
            onClick={() => setShowRecentProjectsModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Recent Projects"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="hidden sm:inline">Recent</span>
          </button>

          {/* Save */}
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Save Project (Ctrl+S)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            <span className="hidden sm:inline">Save</span>
          </button>

          {/* Export */}
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-500 text-white hover:bg-blue-600 rounded-lg transition-colors"
            title="Export (Ctrl+E)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <span className="hidden sm:inline">Export</span>
          </button>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />

          {/* Dark mode toggle */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={isDarkMode ? 'Light mode' : 'Dark mode'}
          >
            {isDarkMode ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Toolbar */}
      <Toolbar
        selectedNodeId={selectedNodeId}
        selectedNodeIds={selectedNodeIds}
        connectingFrom={connectingFrom}
        canUndo={canUndo}
        canRedo={canRedo}
        clipboard={clipboard}
        onAddNode={handleAddNode}
        onAddSwimlane={handleAddSwimlane}
        onConnect={startConnecting}
        onCancelConnect={cancelConnecting}
        onDelete={selectedNodeIds.length > 0 ? deleteSelectedNodes : () => deleteConnection(selectedConnectionId)}
        onUndo={undo}
        onRedo={redo}
        onCopy={copySelectedNodes}
        onPaste={pasteNodes}
        onAutoLayout={handleAutoLayout}
        onAlign={handleAlign}
        onDistribute={handleDistribute}
        onBulkEdit={() => setShowBulkEditModal(true)}
        onCreateGroup={handleCreateGroup}
      />

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Canvas */}
        <Canvas
          ref={canvasSvgRef}
          nodes={nodes}
          connections={connections}
          groups={groups}
          swimlanes={swimlanes}
          selectedNodeIds={selectedNodeIds}
          selectedGroupId={selectedGroupId}
          selectedConnectionId={selectedConnectionId}
          selectedSwimlaneId={selectedSwimlaneId}
          connectingFrom={connectingFrom}
          canvas={canvas}
          onSelectNode={handleSelectNode}
          onSelectGroup={selectGroup}
          onSelectConnection={handleSelectConnection}
          onSelectSwimlane={selectSwimlane}
          onUpdateNode={updateNode}
          onUpdateNodes={updateNodes}
          onUpdateGroup={updateGroup}
          onUpdateSwimlane={updateSwimlane}
          onDeleteNode={deleteNode}
          onDoubleClickNode={handleDoubleClickNode}
          onDoubleClickGroup={handleDoubleClickGroup}
          onDoubleClickConnection={handleDoubleClickConnection}
          onDoubleClickSwimlane={handleDoubleClickSwimlane}
          onReorderSwimlanes={reorderSwimlanes}
          onConnect={handleCompleteConnecting}
          onCanvasClick={handleCanvasClick}
          onZoomChange={handleZoomChange}
          onPanChange={handlePanChange}
          onToggleGrid={toggleGrid}
          isDarkMode={isDarkMode}
        />

        {/* Sidebar */}
        <Sidebar
          nodes={nodes}
          connections={connections}
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />
      </div>

      {/* Modals */}
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onCreateProject={handleCreateProject}
      />

      <EditNodeModal
        isOpen={showEditNodeModal}
        node={selectedNode}
        onClose={() => setShowEditNodeModal(false)}
        onSave={updateNode}
        onDelete={deleteNode}
      />

      <EditConnectionModal
        isOpen={showEditConnectionModal}
        connection={selectedConnection}
        nodes={nodes}
        onClose={() => setShowEditConnectionModal(false)}
        onSave={updateConnection}
        onDelete={deleteConnection}
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        projectName={projectName}
        nodes={nodes}
        connections={connections}
        canvas={canvas}
        svgRef={canvasSvgRef}
      />

      <RecentProjectsModal
        isOpen={showRecentProjectsModal}
        onClose={() => setShowRecentProjectsModal(false)}
        onSelectProject={(name) => {
          // For now, just show the name - in a full implementation
          // you would load from localStorage by project name
          console.log('Selected project:', name);
        }}
      />

      <BulkEditModal
        isOpen={showBulkEditModal}
        selectedNodes={nodes.filter(n => selectedNodeIds.includes(n.id))}
        onClose={() => setShowBulkEditModal(false)}
        onSave={handleBulkEdit}
      />

      <EditGroupModal
        isOpen={showEditGroupModal}
        group={selectedGroup}
        onClose={() => setShowEditGroupModal(false)}
        onSave={updateGroup}
        onDelete={deleteGroup}
      />

      <EditSwimlaneModal
        isOpen={showEditSwimlaneModal}
        swimlane={selectedSwimlane}
        onClose={() => setShowEditSwimlaneModal(false)}
        onSave={updateSwimlane}
        onDelete={deleteSwimlane}
      />
    </div>
  );
};

export default App;
